import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api';
import { Icon } from '../components/icons';
import { ErrorBox, EventStatusBadge, ItemStatusBadge, Loading, PageHead } from '../components/ui';
import { fmtDate, fmtDateTime } from '../format';
import { useI18n } from '../i18n';
import type { EventStatus, Item, ItemStatus } from '../types';
import { ItemFormModal, useOpenLabels } from './Equipment';

interface ItemDetail extends Item {
  kit: { id: string; name: string; qrCode: string } | null;
  bookings: { eventId: string; title: string; status: EventStatus; setupStart: string; teardownEnd: string }[];
  scans: { id: string; type: 'loadout' | 'install' | 'return'; scannedAt: string; eventId: string | null; eventTitle: string | null; by: string }[];
}

export function ItemDetailPage() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const openLabels = useOpenLabels();
  const [editing, setEditing] = useState(false);
  const q = useQuery({ queryKey: ['item', id], queryFn: () => api.get<ItemDetail>(`/items/${id}`) });
  const setStatus = useMutation({
    mutationFn: (status: ItemStatus) => api.patch<Item>(`/items/${id}`, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['item', id] });
      qc.invalidateQueries({ queryKey: ['items'] });
    },
  });

  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error} />;
  const item = q.data;
  const statusActions: ItemStatus[] = item.status === 'out' ? [] : (['available', 'in_repair', 'lost', 'retired'] as ItemStatus[]).filter((s) => s !== item.status);

  return (
    <>
      <PageHead
        back={{ to: '/equipment', label: t('nav.equipment') }}
        title={item.name}
        sub={<span className="mono">{item.qrCode}</span>}
        actions={
          <>
            <button className="btn" onClick={() => openLabels([{ code: item.qrCode, name: item.name }])}>
              <Icon.print />
              {t('equipment.printLabel')}
            </button>
            <button className="btn btn-primary" onClick={() => setEditing(true)}>{t('common.edit')}</button>
          </>
        }
      />
      <div className="grid grid-2">
        <div className="card card-pad stack">
          <div className="spread">
            <h2>{t('common.details')}</h2>
            <ItemStatusBadge status={item.status} />
          </div>
          <dl className="kv">
            <dt>{t('equipment.category')}</dt><dd>{item.category}</dd>
            <dt>{t('equipment.brandModel')}</dt><dd>{[item.brand, item.model].filter(Boolean).join(' ') || '–'}</dd>
            <dt>{t('equipment.serial')}</dt><dd className="mono">{item.serialNumber || '–'}</dd>
            <dt>{t('equipment.case')}</dt>
            <dd>{item.kit ? <Link to={`/kits/${item.kit.id}`}>{item.kit.name}</Link> : '–'}</dd>
            <dt>{t('equipment.location')}</dt>
            <dd>
              {item.status === 'out' && item.locationEventId ? (
                <Link to={`/events/${item.locationEventId}`}>{t('equipment.outOn')}: {item.bookings.find((b) => b.eventId === item.locationEventId)?.title ?? '…'}</Link>
              ) : (
                t('equipment.warehouse')
              )}
            </dd>
            <dt>{t('equipment.purchaseDate')}</dt><dd>{item.purchaseDate ? fmtDate(item.purchaseDate, lang) : '–'}</dd>
            <dt>{t('equipment.purchasePrice')}</dt><dd className="num">{item.purchasePrice ? `€ ${item.purchasePrice}` : '–'}</dd>
            {item.notes && (<><dt>{t('common.notes')}</dt><dd style={{ whiteSpace: 'pre-line' }}>{item.notes}</dd></>)}
          </dl>
        </div>
        <div className="card card-pad stack">
          <h2>{t('equipment.changeStatus')}</h2>
          {item.status === 'out' ? (
            <p className="muted" style={{ margin: 0 }}>{t('equipment.outHint')}</p>
          ) : (
            <>
              <p className="muted" style={{ margin: 0 }}>{t('equipment.statusHint')}</p>
              <div className="row">
                {statusActions.map((s) => (
                  <button key={s} className={`btn ${s === 'lost' ? 'btn-danger' : s === 'available' ? 'btn-good' : ''}`} onClick={() => setStatus.mutate(s)} disabled={setStatus.isPending}>
                    {t(`equipment.markAs.${s}`)}
                  </button>
                ))}
              </div>
            </>
          )}
          <ErrorBox error={setStatus.error} />
        </div>
      </div>

      <div className="grid grid-2" style={{ marginTop: 16 }}>
        <div className="card">
          <div className="card-head"><h2>{t('equipment.bookings')}</h2></div>
          {item.bookings.length === 0 ? (
            <div className="card-body muted">{t('equipment.noBookings')}</div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <tbody>
                  {item.bookings.map((b) => (
                    <tr key={b.eventId}>
                      <td><Link to={`/events/${b.eventId}`}>{b.title}</Link></td>
                      <td className="muted nowrap">{fmtDate(b.setupStart, lang)}</td>
                      <td><EventStatusBadge status={b.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="card">
          <div className="card-head"><h2>{t('equipment.scanHistory')}</h2></div>
          {item.scans.length === 0 ? (
            <div className="card-body muted">{t('equipment.noScans')}</div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <tbody>
                  {item.scans.map((s) => (
                    <tr key={s.id}>
                      <td className="nowrap">{fmtDateTime(s.scannedAt, lang)}</td>
                      <td><span className="badge">{t(`scan.${s.type}`)}</span></td>
                      <td>{s.eventId ? <Link to={`/events/${s.eventId}`}>{s.eventTitle}</Link> : '–'}</td>
                      <td className="muted">{s.by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {editing && <ItemFormModal item={item} onClose={() => setEditing(false)} />}
    </>
  );
}
